import { useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';
import CoursePromoCard from '@components/shared/bytes/course-promo-card/CoursePromoCard';
import BytesFooter from '@components/shared/bytes/bytes-footer/BytesFooter';
import ModuleLessonRoute, {
  BottomBar,
  CourseOutlineBox,
} from '@components/shared/bytes/ModuleLessonRoute';
import { getArticles, getBytesStrapiUrl } from '@lib/bytesStrapiServices';
import axios from 'axios';
import ReadProgressBar from '@components/reuseable/ReadProgressBar';
import SiteHeader from '@components/shared/SiteHeader';
import MarkDownPreview from '@components/reuseable/MarkDownPreview';
import CompilerPromoCard from '@components/shared/bytes/CompilerPromoCard';
import moment from 'moment';

const ShareButtons = dynamic(() => import('@components/shared/bytes/share-buttons/ShareButtons'), {
  ssr: false,
});
const RelatedContentCard = dynamic(() =>
  import('@components/shared/bytes/related-content-card/RelatedContentCard')
);

const ArticleCard = dynamic(() => import('@components/shared/bytes/article-card/ArticleCard'), {
  ssr: false,
});

const TutorialCard = dynamic(
  () => import('@components/shared/bytes/bytes-tutorials/TutorialCard'),
  {
    ssr: false,
  }
);

const Lesson = ({ lessonData, tutorialData, articlesData }) => {
  const filterRef = useRef(null);
  const divRef = useRef(null);
  const { modules: modulesData, title, compiler } = tutorialData?.data?.attributes || {};
  const { relatedArticles, relatedPrograms, relatedTutorials } = tutorialData?.meta || {};
  const {
    title: lessonTitle,
    body,
    heroImage,
    dbLocation,
    lastUpdatedAt,
    updatedAt,
  } = lessonData?.data?.attributes || {};
  const { metaTitle, metaDescription, canonicalURL, structuredData, metaImage, metaRobots } =
    lessonData?.data?.attributes?.seo || {};

  const moduleLessons = {};
  let modules = {};

  modulesData?.data?.forEach((module) => {
    const moduleName = module.attributes.name;
    modules = { ...modules, [module.attributes.serialNo]: module.attributes.name };
    const lessons = module.attributes.lessons.data.sort(
      (a, b) => a.attributes.serialNo - b.attributes.serialNo
    );
    const moduleShortLessons = lessons.map((lesson) => {
      return {
        title: lesson.attributes.title,
        slug: lesson.attributes.slug,
        serialNo: lesson.attributes.serialNo,
      };
    });

    moduleLessons[moduleName] = moduleShortLessons;
  });

  const moduleList = Object.entries(modules)
    .sort(([keyA], [keyB]) => keyA - keyB)
    .map(([key, value], index) => ({ id: index + 1, name: value }));

  const markdownRef = useRef();

  useEffect(() => {
    const handleScroll = () => {
      const divHeight = markdownRef.current.offsetHeight;
      const scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

      if (scrollPosition > divHeight) {
        filterRef.current.classList.add('hidden');
      } else {
        filterRef.current.classList.remove('hidden');
      }
    };

    const requestAnimationFrameId = window.requestAnimationFrame(() => {
      document.addEventListener('scroll', handleScroll);
    });

    return () => {
      document.removeEventListener('scroll', handleScroll);
      window.cancelAnimationFrame(requestAnimationFrameId);
    };
  }, []);

  return (
    <>
      <SiteHeader
        image={metaImage?.data?.attributes?.url}
        title={metaTitle}
        desc={metaDescription}
        canonicalUrl={canonicalURL}
        structuredData={structuredData}
        metaRobots={metaRobots}
      />
      <ReadProgressBar elementRef={markdownRef} />
      <ModuleLessonRoute moduleLessons={moduleLessons} moduleList={moduleList} decoupled>
        <section>
          <div className="grid gap-y-[24px]  gap-x-[32px] px-[16px] pt-[20px] pb-[50px] md:grid-cols-12 md:px-[124px] md:pt-[40px] md:pb-[100px]">
            <div className="sticky top-16 hidden w-full flex-col items-start gap-y-[16px] self-start md:col-span-4 md:flex">
              <CourseOutlineBox />
              <ShareButtons grayout />
            </div>
            <div ref={markdownRef} className="w-full font-satoshi-regular md:col-span-8">
              <h1 className="font-satoshi-bold font-satoshi-bold text-[36px] font-bold  leading-10 tracking-wide">
                {lessonTitle}
              </h1>
              <span className="mb-4 font-satoshi-regular text-[14px] leading-5">
                Last Updated: {moment(lastUpdatedAt || updatedAt).format('Do MMMM, YYYY')}
              </span>
              <MarkDownPreview content={body} dbLocation={dbLocation} />
            </div>
            <div className="md:hidden">
              <ShareButtons grayout />
            </div>
          </div>

          <div className="sticky bottom-0 z-30 hidden md:block">
            <BottomBar />
          </div>

          <div className="sticky bottom-0 z-30 md:hidden" ref={filterRef}>
            <BottomBar>
              <div className="absolute bottom-full w-full">
                <CourseOutlineBox boxStyle={'relative z-10'} />
              </div>
            </BottomBar>
          </div>

          <div className="flex w-full flex-col gap-y-16 bg-[#FAFAFA] py-12 px-4 md:border-t md:border-[#E0E0E0] md:bg-transparent md:px-[124px] md:py-20">
            <div className="flex flex-col items-center gap-y-16 md:items-start">
              <p className="font-satoshi-regular font-satoshi-bold text-[30px] font-bold  leading-9">
                Top Tutorials
              </p>
              <div className="grid gap-8 md:grid-cols-3">
                {relatedTutorials?.slice(0, 3).map(({ id, ...attributes }) => (
                  <TutorialCard key={id} {...attributes} />
                ))}
              </div>
            </div>

            <div className="flex flex-col items-center gap-y-16 md:items-start">
              <p className="font-satoshi-regular font-satoshi-bold text-[30px] font-bold  leading-9">
                Related Articles
              </p>
              <div className="grid gap-8 md:grid-cols-3">
                {relatedArticles?.slice(0, 3).map(({ id, ...attributes }) => (
                  <ArticleCard key={id} {...attributes} />
                ))}
              </div>
            </div>
          </div>
        </section>
      </ModuleLessonRoute>
    </>
  );
};
export default Lesson;

export async function getStaticProps({ params }) {
  const { lesson, slug } = params;
  try {
    const apiBaseUrl = getBytesStrapiUrl();
    const lessonData = await axios.get(
      `${apiBaseUrl}/api/lessons/${lesson}?populate=heroImage,seo,seo.metaImage`
    );
    const tutorialData = await axios.get(
      `${apiBaseUrl}/api/tutorials/${slug}?populate=modules.lessons,compiler`
    );

    const articles = await getArticles({ page: 1, size: 9 });

    if (!tutorialData?.data?.data) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        articlesData: articles?.data || {},
        tutorialData: tutorialData?.data || {},
        lessonData: lessonData?.data || {},
      },
      revalidate: 1800,
    };
  } catch (error) {
    console.log(error);
    return {
      notFound: true,
    };
  }
}

export async function getStaticPaths() {
  const apiBaseUrl = getBytesStrapiUrl();
  const {
    data: { data: tutorials },
  } = await axios.get(`${apiBaseUrl}/api/tutorials?fields[0]=slug&populate=modules.lessons`);

  const paths = tutorials
    .filter(({ attributes }) => attributes.slug)
    .flatMap(
      ({
        attributes: {
          slug: tutorialSlug,
          modules: { data: modulesOfTutorial },
        },
      }) => {
        return modulesOfTutorial.flatMap((module) => {
          return module.attributes.lessons.data.map(
            ({ attributes: { slug: lessonSlug, views: lessonViews } }) => ({
              params: { slug: tutorialSlug, lesson: lessonSlug, views: lessonViews },
            })
          );
        });
      }
    );
  const trimmedPaths = paths.sort((a, b) => b.params.views - a.params.views).slice(0, 20);

  return {
    paths: trimmedPaths,
    fallback: 'blocking',
  };
}
